import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { Currency } from '@vy/types';
import { ReactNode } from 'react';
import { contractTableStyles } from './contract-table-styles';

export type BaseRowData = {
  id: string;
  isParent?: boolean;
  isChild?: boolean;
  isExpanded?: boolean;
  hidden?: boolean;
  toggleExpand?: (id: string) => void;
};

type ContractTableProps<TRow extends BaseRowData> = {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  currency: Currency;
  renderCell: (row: TRow, column: GridColDef<TRow>, currency: Currency) => ReactNode;
  hasExpandableRows?: boolean;
  firstColumnField?: string;
};

export const ContractTable = <TRow extends BaseRowData>({
  rows,
  columns,
  currency,
  renderCell,
  hasExpandableRows = false,
  firstColumnField,
}: ContractTableProps<TRow>): JSX.Element => {
  const visibleRows = hasExpandableRows ? rows.filter((row) => !row.hidden) : rows;

  const hasParentRows = hasExpandableRows && rows.some((row) => row.isParent);

  const dataColumns = hasExpandableRows ? columns.slice(1) : columns;

  return (
    <Box sx={contractTableStyles.tableContainer}>
      <Table sx={contractTableStyles.table}>
        <TableHead>
          <TableRow>
            {hasParentRows && (
              <TableCell
                sx={{
                  ...contractTableStyles.tableHeader,
                  width: 48,
                  minWidth: 48,
                  maxWidth: 48,
                  padding: '8px',
                }}
              />
            )}

            {dataColumns.map((column, index) => (
              <TableCell
                key={column.field}
                sx={{
                  ...contractTableStyles.tableHeader,
                  ...(index === 0 ? { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' } : {}),
                }}
              >
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleRows.map((row) => (
            <TableRow
              key={row.id}
              sx={{
                ...contractTableStyles.tableRow,
                ...(row.isChild ? contractTableStyles.childRow : {}),
              }}
            >
              {hasParentRows && (
                <TableCell
                  sx={{
                    ...contractTableStyles.tableCell,
                    width: 48,
                    minWidth: 48,
                    maxWidth: 48,
                    padding: '8px',
                  }}
                >
                  {row.isParent && (
                    <IconButton
                      size="small"
                      onClick={() => row.toggleExpand?.(row.id)}
                      sx={contractTableStyles.expandIcon}
                    >
                      {row.isExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </TableCell>
              )}

              {dataColumns.map((column, index) => {
                const isFirstColumn = index === 0;
                const isFirstColumnAndChildRow =
                  hasExpandableRows && row.isChild && firstColumnField && column.field === firstColumnField;

                return (
                  <TableCell
                    key={column.field}
                    sx={{
                      ...contractTableStyles.tableCell,
                      ...(isFirstColumn
                        ? {
                            width: 240,
                            minWidth: 240,
                            maxWidth: 240,
                            textAlign: 'left',
                          }
                        : {}),
                      ...(isFirstColumnAndChildRow ? { paddingLeft: '48px' } : {}),
                    }}
                  >
                    {renderCell(row, column, currency)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
